import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // Assuming api service is set up
// MUI Imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import StarIcon from '@mui/icons-material/Star'; // For recommended pathways

function TreatmentPathwaysViewer({ token, therapistSpecializations }) {
    const [pathways, setPathways] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchPathways = async () => {
            setLoading(true);
            setError('');
            try {
                const data = await api.getPathwaysList(token);
                setPathways(data || []);
            } catch (err) {
                console.error("Error fetching treatment pathways:", err);
                setError(err.message || 'Failed to load treatment pathways.');
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            fetchPathways();
        } else {
            setError('Authentication token not found.');
            setLoading(false);
        }
    }, [token]);

    // Function to check if a pathway is recommended based on therapist specializations
    const isRecommended = (pathwayModalities) => {
        if (!therapistSpecializations || therapistSpecializations.length === 0 || !pathwayModalities || pathwayModalities.length === 0) {
            return false; // Cannot recommend if therapist has no specs or pathway has no modalities
        }
        // Check if there's at least one overlapping modality
        return therapistSpecializations.some(spec => pathwayModalities.includes(spec));
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Treatment Pathways Guide</Typography>
            {pathways.length === 0 ? (
                <Typography>No treatment pathways defined in the platform settings.</Typography>
            ) : (
                pathways.map((pathway, index) => {
                    const recommended = isRecommended(pathway.suitableModalities);
                    return (
                        <Accordion key={index} sx={{ mb: 1 }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`pathway-${index}-content`}
                                id={`pathway-${index}-header`}
                                sx={{ backgroundColor: recommended ? 'success.light' : 'grey.100' }} // Highlight recommended
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontWeight: 'medium' }}>
                                        {pathway.name}
                                        {recommended && <StarIcon fontSize="small" sx={{ color: 'warning.main', verticalAlign: 'middle', ml: 0.5 }} />}
                                    </Typography>
                                    {pathway.suitableModalities && pathway.suitableModalities.length > 0 && (
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {pathway.suitableModalities.map(mod => (
                                                <Chip key={mod} label={mod} size="small" variant="outlined" />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ mb: 2 }}>{pathway.description}</Typography>
                                <Typography variant="subtitle2" gutterBottom>Phases:</Typography>
                                {pathway.phases && pathway.phases.length > 0 ? (
                                    <List dense disablePadding>
                                        {pathway.phases.map((phase, phaseIndex) => (
                                            <ListItem key={phaseIndex} disableGutters>
                                                <ListItemText
                                                    primary={`${phase.name} (${phase.defaultDuration || 'N/A'} weeks)`}
                                                    secondary={phase.description}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No phases defined for this pathway.</Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })
            )}
        </Box>
    );
}

export default TreatmentPathwaysViewer;
